import { Category } from "@CommonApi/categories";
import { useState } from "react";
import { Button, Flex, Text } from "src/elements";
import { SubCategory } from "./SubCategory";
import { routeToUrl } from "@CommonUtils/routes";
import { useImportStoreSummaryModal } from "src/modals/ImportStoreSummary/store/hooks";
import { useImportVariantRestrictionModal } from "src/modals/ImportVariantRestriction/store/hooks";
import { useDescribeVariantRestrictionModal } from "src/modals/DescribeVariantRestriction/store/hooks";
import { useImportProductImagesModal } from "src/modals/ImportProductImages/store/hooks";
import { useImportJdaBaselineModal } from "src/modals/ImportJdaBaseline/store/hooks";
import { pages } from "src/utils";
import { useNavigation } from "src/utils/navigation";

interface Props {
  readonly category: string;
  readonly subcategories: Category[];
  readonly editCategory: (category: Category) => void;
  readonly onStatusChange: () => void;
  readonly isActiveByDefault?: boolean;
  readonly isJdaImportEnabled: boolean;
}

export const CategoryGroup = ({
  category,
  subcategories,
  editCategory,
  onStatusChange,
  isActiveByDefault,
  isJdaImportEnabled,
}: Props) => {
  const { openImportStoreSummaryModal } = useImportStoreSummaryModal();
  const { openImportVariantRestrictionModal } = useImportVariantRestrictionModal();
  const { openDescribeVariantRestrictionModal } = useDescribeVariantRestrictionModal();
  const { openImportProductImagesModal } = useImportProductImagesModal();
  const { openImportJdaBaselineModal } = useImportJdaBaselineModal();
  const { navigate } = useNavigation();

  const [isActive, setIsActive] = useState(isActiveByDefault);

  const handleImportStoreSummary = (category: Category) => {
    openImportStoreSummaryModal(category);
  };

  const handleImportVariantRestriction = (category: Category) => {
    openImportVariantRestrictionModal(category);
  };

  const handleDescribeVariantRestriction = (category: Category) => {
    openDescribeVariantRestrictionModal(category);
  };

  const handleImportProductImages = () => {
    openImportProductImagesModal();
  };

  const handleImportJdaBaseline = (category: Category) => {
    openImportJdaBaselineModal(category);
  };

  const navigateToCategoryStoreFixtures = (categoryId: number) => {
    navigate(
      routeToUrl(pages.categories_categoryId_storeFixtures, {
        categoryId: categoryId.toString(),
      }),
    );
  };

  return (
    <Flex column borderRadius="3px" boxShadow="0 10px 25px rgba(36, 36, 36, 0.2)">
      <Flex justify="between" align="center" padding="10px" onClick={() => setIsActive(!isActive)}>
        <Text variant="body1">{category}</Text>

        <Button
          name="group-toggle"
          variant="borderless"
          iconRight={{ name: isActive ? "triangleUp" : "triangleDown" }}
        />
      </Flex>

      {isActive && (
        <Flex column gap="10px">
          {subcategories.map((subcategory) => (
            <SubCategory
              key={subcategory.id}
              onEdit={editCategory}
              category={subcategory}
              onImportStoreSummary={handleImportStoreSummary}
              onImportVariantRestriction={handleImportVariantRestriction}
              onDescribeVariantRestriction={handleDescribeVariantRestriction}
              onImportProductImages={handleImportProductImages}
              onImportJdaBaseline={handleImportJdaBaseline}
              isJdaImportEnabled={isJdaImportEnabled}
              onStoreFixture={(category) => navigateToCategoryStoreFixtures(category.id)}
              onStatusChange={onStatusChange}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
};
